import * as signalR from '@microsoft/signalr';
import { HubConnection, HubConnectionState } from '@microsoft/signalr';

const API_BASE_URL = import.meta.env.VITE_API_GESTION_CASOS || '';
const HUB_URL = `${API_BASE_URL}/hubs/notifications`;

const STORAGE_KEYS = {
  CONNECTION_STATE: 'signalr_connection_state',
  FAILED_MESSAGES: 'signalr_failed_messages',
  LAST_CONNECTION_ID: 'signalr_last_connection_id'
};

export interface SignalRMessage {
  type: string;
  data: any;
  timestamp: string;
}

export interface FailedMessage {
  method: string;
  args: any[];
  timestamp: number;
  retryCount: number;
}

export interface ConnectionMetrics {
  connectedAt: number | null;
  disconnectedAt: number | null;
  reconnectCount: number;
  messagesReceived: number;
  messagesSent: number;
  averageLatency: number;
  lastHeartbeat: number | null;
}

export class SignalRService {
  private static instance: SignalRService;
  private connection: HubConnection | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10; // Aumentado de 5 a 10
  private listeners: Map<string, Set<Function>> = new Map();
  private failedMessages: FailedMessage[] = [];
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private metrics: ConnectionMetrics = {
    connectedAt: null,
    disconnectedAt: null,
    reconnectCount: 0,
    messagesReceived: 0,
    messagesSent: 0,
    averageLatency: 0,
    lastHeartbeat: null
  };
  private latencies: number[] = [];
  private currentToken: string | null = null;
  private isReconnecting = false;

  private constructor() {
    this.loadFailedMessagesFromStorage();
    this.setupStorageSync();
  }

  static getInstance(): SignalRService {
    if (!SignalRService.instance) {
      SignalRService.instance = new SignalRService();
    }
    return SignalRService.instance;
  }

  /**
   * Conectar a SignalR con mejor manejo de reconexión y fallback
   */
  async connect(token: string): Promise<boolean> {
    if (this.connection?.state === HubConnectionState.Connected) {
      console.log('[SignalR] Ya está conectado');
      return true;
    }

    if (this.isReconnecting) {
      console.log('[SignalR] Reconexión en progreso, esperando...');
      return false;
    }

    this.currentToken = token;

    try {
      // Intentar con WebSockets primero, luego fallback a ServerSentEvents y LongPolling
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(HUB_URL, {
          accessTokenFactory: () => this.currentToken || token,
          transport: signalR.HttpTransportType.WebSockets |
                     signalR.HttpTransportType.ServerSentEvents |
                     signalR.HttpTransportType.LongPolling,
          skipNegotiation: false,
          withCredentials: true
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            if (retryContext.previousRetryCount >= this.maxReconnectAttempts) {
              console.warn('[SignalR] Máximo de reintentos alcanzado');
              return null; // Detener reconexión automática
            }
            // Backoff exponencial: 0s, 2s, 4s, 8s, 16s, 32s, hasta max 60s
            const delay = Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 60000);
            console.log(`[SignalR] Reintentando en ${delay / 1000}s (intento ${retryContext.previousRetryCount + 1}/${this.maxReconnectAttempts})`);
            return delay;
          }
        })
        .configureLogging(import.meta.env.DEV ? signalR.LogLevel.Debug : signalR.LogLevel.Warning)
        .build();

      this.setupEventHandlers();

      await this.connection.start();

      console.log('[SignalR] Conectado exitosamente');
      console.log('[SignalR] Transport:', this.connection.baseUrl);
      console.log('[SignalR] Connection ID:', this.connection.connectionId);

      this.reconnectAttempts = 0;
      this.metrics.connectedAt = Date.now();
      this.metrics.disconnectedAt = null;

      // Guardar estado
      this.saveConnectionState();

      // Iniciar heartbeat
      this.startHeartbeat();

      // Reenviar mensajes fallidos
      await this.retryFailedMessages();

      return true;
    } catch (error) {
      console.error('[SignalR] Error conectando:', error);
      this.reconnectAttempts++;
      this.metrics.disconnectedAt = Date.now();

      // Si falla, intentar reconexión manual después de un delay
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        const delay = Math.min(2000 * this.reconnectAttempts, 30000);
        console.log(`[SignalR] Reintentando conexión en ${delay / 1000}s`);
        setTimeout(() => this.reconnect(), delay);
      }

      return false;
    }
  }

  /**
   * Reconexión manual con mejor manejo
   */
  private async reconnect(): Promise<void> {
    if (this.isReconnecting || this.connection?.state === HubConnectionState.Connected) {
      return;
    }

    this.isReconnecting = true;
    console.log('[SignalR] Iniciando reconexión manual...');

    try {
      if (this.currentToken) {
        await this.connect(this.currentToken);
        this.metrics.reconnectCount++;
      } else {
        console.error('[SignalR] No hay token disponible para reconexión');
      }
    } catch (error) {
      console.error('[SignalR] Error en reconexión manual:', error);
    } finally {
      this.isReconnecting = false;
    }
  }

  /**
   * Setup de event handlers mejorado
   */
  private setupEventHandlers(): void {
    if (!this.connection) return;

    this.connection.onclose((error) => {
      console.log('[SignalR] Conexión cerrada', error);
      this.metrics.disconnectedAt = Date.now();
      this.stopHeartbeat();
      this.saveConnectionState();
      this.notifyListeners('connectionClosed', { error });

      // Intentar reconexión si hay token
      if (this.currentToken && this.reconnectAttempts < this.maxReconnectAttempts) {
        setTimeout(() => this.reconnect(), 5000);
      }
    });

    this.connection.onreconnecting((error) => {
      console.log('[SignalR] Reconectando...', error);
      this.stopHeartbeat();
      this.notifyListeners('reconnecting', { error, attempt: this.reconnectAttempts });
    });

    this.connection.onreconnected((connectionId) => {
      console.log('[SignalR] Reconectado con ID:', connectionId);
      this.reconnectAttempts = 0;
      this.metrics.reconnectCount++;
      this.metrics.connectedAt = Date.now();
      this.saveConnectionState();
      this.startHeartbeat();
      this.notifyListeners('reconnected', { connectionId });

      // Reenviar mensajes fallidos
      this.retryFailedMessages();
    });

    // Eventos de notificación
    this.connection.on('ReceiveNotification', (notification: any) => {
      console.log('[SignalR] Notificación recibida:', notification);
      this.metrics.messagesReceived++;
      this.notifyListeners('notification', notification);
    });

    this.connection.on('ReceiveMessage', (message: string) => {
      console.log('[SignalR] Mensaje recibido:', message);
      this.metrics.messagesReceived++;
      this.notifyListeners('message', { message });
    });

    this.connection.on('CaseUpdated', (update: any) => {
      console.log('[SignalR] Actualización de caso:', update);
      this.metrics.messagesReceived++;
      this.notifyListeners('caseUpdated', update);
    });

    this.connection.on('NewCase', (caseData: any) => {
      console.log('[SignalR] Nuevo caso:', caseData);
      this.metrics.messagesReceived++;
      this.notifyListeners('newCase', caseData);
    });

    this.connection.on('CaseStatusChanged', (statusData: any) => {
      console.log('[SignalR] Estado de caso cambiado:', statusData);
      this.metrics.messagesReceived++;
      this.notifyListeners('caseStatusChanged', statusData);
    });

    // Evento de heartbeat
    this.connection.on('Heartbeat', (timestamp: number) => {
      const latency = Date.now() - timestamp;
      this.latencies.push(latency);
      if (this.latencies.length > 10) {
        this.latencies.shift();
      }
      this.metrics.averageLatency = this.latencies.reduce((a, b) => a + b, 0) / this.latencies.length;
      this.metrics.lastHeartbeat = Date.now();
    });
  }

  /**
   * Heartbeat monitoring
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();

    this.heartbeatInterval = setInterval(() => {
      if (this.connection?.state === HubConnectionState.Connected) {
        this.connection.invoke('Heartbeat', Date.now()).catch((error) => {
          console.error('[SignalR] Error en heartbeat:', error);
        });
      }
    }, 30000); // Cada 30 segundos
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Desconectar
   */
  async disconnect(): Promise<void> {
    if (this.connection) {
      try {
        this.stopHeartbeat();
        await this.connection.stop();
        console.log('[SignalR] Desconectado correctamente');
        this.metrics.disconnectedAt = Date.now();
        this.saveConnectionState();
      } catch (error) {
        console.error('[SignalR] Error desconectando:', error);
      }
      this.connection = null;
      this.currentToken = null;
    }
  }

  /**
   * Estado de conexión
   */
  isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }

  getConnectionState(): HubConnectionState | null {
    return this.connection?.state ?? null;
  }

  getConnectionId(): string | null {
    return this.connection?.connectionId ?? null;
  }

  getMetrics(): ConnectionMetrics {
    return { ...this.metrics };
  }

  /**
   * Event listeners
   */
  on(event: string, callback: Function): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  off(event: string, callback: Function): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.delete(callback);
    }
  }

  private notifyListeners(event: string, data: any): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error(`[SignalR] Error en listener de evento ${event}:`, error);
        }
      });
    }
  }

  /**
   * Invocar métodos con retry automático
   */
  private async invokeWithRetry(method: string, ...args: any[]): Promise<any> {
    if (this.connection?.state === HubConnectionState.Connected) {
      try {
        const startTime = Date.now();
        const result = await this.connection.invoke(method, ...args);
        const latency = Date.now() - startTime;

        this.latencies.push(latency);
        if (this.latencies.length > 10) {
          this.latencies.shift();
        }
        this.metrics.averageLatency = this.latencies.reduce((a, b) => a + b, 0) / this.latencies.length;
        this.metrics.messagesSent++;

        return result;
      } catch (error) {
        console.error(`[SignalR] Error invocando ${method}:`, error);

        // Guardar mensaje fallido para retry posterior
        this.addFailedMessage(method, args);
        throw error;
      }
    } else {
      console.warn(`[SignalR] No conectado, guardando mensaje para retry: ${method}`);
      this.addFailedMessage(method, args);
      throw new Error('SignalR no está conectado');
    }
  }

  /**
   * Métodos de grupos y mensajes
   */
  async joinCaseGroup(caseId: string): Promise<void> {
    await this.invokeWithRetry('JoinCaseGroup', caseId);
    console.log(`[SignalR] Unido al grupo del caso: ${caseId}`);
  }

  async leaveCaseGroup(caseId: string): Promise<void> {
    await this.invokeWithRetry('LeaveCaseGroup', caseId);
    console.log(`[SignalR] Salido del grupo del caso: ${caseId}`);
  }

  async sendMessage(userId: string, message: string): Promise<void> {
    await this.invokeWithRetry('SendMessageToUser', userId, message);
  }

  /**
   * Queue de mensajes fallidos
   */
  private addFailedMessage(method: string, args: any[]): void {
    const failedMessage: FailedMessage = {
      method,
      args,
      timestamp: Date.now(),
      retryCount: 0
    };

    this.failedMessages.push(failedMessage);
    this.saveFailedMessagesToStorage();
  }

  private async retryFailedMessages(): Promise<void> {
    if (this.failedMessages.length === 0) return;

    console.log(`[SignalR] Reenviando ${this.failedMessages.length} mensajes fallidos...`);

    const messages = [...this.failedMessages];
    this.failedMessages = [];

    for (const message of messages) {
      try {
        await this.invokeWithRetry(message.method, ...message.args);
        console.log(`[SignalR] Mensaje reenviado exitosamente: ${message.method}`);
      } catch (error) {
        console.error(`[SignalR] Error reenviando mensaje ${message.method}:`, error);

        // Si falla y no ha reintentado mucho, volver a agregar
        if (message.retryCount < 3) {
          message.retryCount++;
          this.failedMessages.push(message);
        } else {
          console.warn(`[SignalR] Mensaje ${message.method} descartado después de 3 reintentos`);
        }
      }
    }

    this.saveFailedMessagesToStorage();
  }

  /**
   * Persistencia en localStorage
   */
  private saveConnectionState(): void {
    try {
      const state = {
        connectionId: this.connection?.connectionId,
        connectedAt: this.metrics.connectedAt,
        disconnectedAt: this.metrics.disconnectedAt,
        reconnectCount: this.metrics.reconnectCount
      };
      localStorage.setItem(STORAGE_KEYS.CONNECTION_STATE, JSON.stringify(state));
    } catch (error) {
      console.error('[SignalR] Error guardando estado:', error);
    }
  }

  private saveFailedMessagesToStorage(): void {
    try {
      localStorage.setItem(STORAGE_KEYS.FAILED_MESSAGES, JSON.stringify(this.failedMessages));
    } catch (error) {
      console.error('[SignalR] Error guardando mensajes fallidos:', error);
    }
  }

  private loadFailedMessagesFromStorage(): void {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.FAILED_MESSAGES);
      if (saved) {
        this.failedMessages = JSON.parse(saved);
        console.log(`[SignalR] ${this.failedMessages.length} mensajes fallidos cargados desde storage`);
      }
    } catch (error) {
      console.error('[SignalR] Error cargando mensajes fallidos:', error);
      this.failedMessages = [];
    }
  }

  private setupStorageSync(): void {
    // Sincronizar entre pestañas
    window.addEventListener('storage', (event) => {
      if (event.key === STORAGE_KEYS.FAILED_MESSAGES) {
        this.loadFailedMessagesFromStorage();
      }
    });
  }

  /**
   * Limpiar recursos
   */
  clearFailedMessages(): void {
    this.failedMessages = [];
    localStorage.removeItem(STORAGE_KEYS.FAILED_MESSAGES);
  }

  clearStorage(): void {
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  }
}

export const signalRService = SignalRService.getInstance();
